<template>
  <div>
    <div class="page-title-header">
      <div class="page-header">
        <b-breadcrumb class="m-0">
          <b-breadcrumb-item
            :to="{
              name: 'DashboardHome',
            }"
          >
            <i class="fa fa-home"></i>
          </b-breadcrumb-item>
          <b-breadcrumb-item active>全系統設定</b-breadcrumb-item>
          <b-breadcrumb-item active>全模組列表</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>

    <b-card>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h4 class="font-weight-bold">全模組列表</h4>
        <div class="d-flex align-items-center" style="width: 60%">
          <AppSingleSelect
            class="mr-1"
            v-model="selectedTable"
            :options="tableOptions"
            @input="tableChange"
          ></AppSingleSelect>
          <b-button
            v-if="checkPermission([consts.ORGANIZATION_MODULE_CREATE])"
            class="flex-shrink-0 mr-2"
            variant="primary"
            :to="{ name: 'OrganizationModuleCreate' }"
          ><i class="fa fa-plus"></i>新增模組</b-button
          >
          <b-input-group class="ml-0 ml-xl-4">
            <b-form-input
              v-model="query.keyword"
              v-on:keyup.enter="handlePageReset"
              placeholder="請輸入模組名稱"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-card-text>
        <b-overlay :show="showLoading" rounded="sm">
          <b-table
            striped
            hover
            responsive
            :items="organizationModules"
            :fields="fields"
            @sort-changed="handleSort"
          >
            <template #cell(name)="data">
              <div v-if="data.item.table !=='modules'">
                {{data.item.name}}
              </div>
              <router-link
                v-else
                :to="{
                  name: 'OrganizationModuleChildrenList',
                  params: { parent_id: data.item.id, table: data.item.table }
                }">
                {{ data.item.name }}
              </router-link>
            </template>
            <template #cell(actions)="data">
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.ORGANIZATION_MODULE_LIST_VIEW])"
                variant="inverse-primary"
                :to="{
                  name: 'OrganizationModuleView',
                  params: { organization_module_id: data.item.id },
                }"
              >
                <span class="mdi mdi-eye"></span> 查看
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.ORGANIZATION_MODULE_MODIFY])"
                variant="inverse-warning"
                :to="{
                  name: 'OrganizationModuleEdit',
                  params: { organization_module_id: data.item.id }
                }"
              >
                <span class="mdi mdi-lead-pencil"></span> 編輯
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.ORGANIZATION_MODULE_DELETE])"
                variant="inverse-danger"
                @click="deleteOrganizationModule(data.item)"
              >
                <span class="mdi mdi-delete"></span> 刪除
              </b-button>
            </template>
          </b-table>
        </b-overlay>
        <CustomPagination
          :currentPage.sync="query.page"
          :totalRows="totalRows"
          :perPage.sync="query.per_page"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import {organizationModuleApi} from "@/apis/organization-module";
import { mapGetters, mapState } from "vuex";
import CopyUrlButton from "@/components/Dashboard/CopyUrlButton.vue";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import generateLiffUrl from "@/mixins/Dashboard/liffUrlHelper";
import sweetAlertPresets from "@/mixins/Dashboard/sweetAlertPresets";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import VueQr from "vue-qr";
import AppSingleSelect from "@/components/AppSingleSelect.vue";

export default {
  components: {
    AppSingleSelect,
    CopyUrlButton,
    CustomPagination,
    VueQr,
  },
  mixins: [generateLiffUrl, sweetAlertPresets, paginationMixin],
  data() {
    return {
      consts: consts,
      showLoading: true,
      organizationModules: [],
      fields: [
        { key: "table", label: "設定類型" },
        { key: "key", label: "代碼" },
        { key: "type", label: "資料類型" },
        { key: "category", label: "分類" },
        { key: "name", label: "名稱" },
        { key: "description", label: "內容描述" },
        { key: "target", label: "系統方案代碼" },
        { key: "actions", label: "管理" }
      ],
      totalRows: 0,
      initialized: false,
      query: {
        table: "default",
        per_page: 10,
        page: 1,
        sort_by: "created_at",
        order_by: "desc",
        keyword: "",
      },
      selectedTable: {},
    };
  },
  computed: {
    ...mapGetters("general", ["myBranches", "currentBranch"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    tableOptions() {
      return [
        {
          value: "default",
          text: "請選擇設定類型",
        },
        {
          value: "modules",
          text: "模組",
        },
        {
          value: "organization_variables",
          text: "組織參數",
        },
        {
          value: "templates",
          text: "通知樣板",
        },
        {
          value: "chatbot_keywords",
          text: "關鍵字  ",
        },
      ];
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route() {
      if (!this.initialized) return;
      this.fetchOrganizationModules();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    if (typeof this.$route.params.table !== "undefined") {
      this.query.table = this.$route.params.table;
    }
    if (this.query.table != null) {
      this.selectedTable = this.tableOptions.find(
        (option) => option.value === this.query.table
      )
    }

    await this.fetchOrganizationModules();
    this.initialized = true;
    this.showLoading = false;
  },
  methods: {
    tableChange() {
      this.query.table = this.selectedTable?.value ?? null;

      this.handlePageReset();
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchOrganizationModules() {
      this.showLoading = true;
      try {
        const params = { ...this.query };

        if (params.table === 'default') {
          this.organizationModules = [];
          this.totalRows = 0;
        } else {
          const response = await organizationModuleApi.list(params);
          this.organizationModules = response.data.data;
          this.totalRows = response.data.meta.total;
        }

        this.showLoading = false;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取模組列表失敗", "error");

        this.showLoading = false;
      }
    },
    async deleteOrganizationModule(organizationModule) {
      this.showLoading = true;
      try {
        if (
          false ===
          (await this.confirmDeletion({
            text: `模組名稱：${organizationModule.name}`,
          }))
        ) {
          return;
        }

        await organizationModuleApi.remove(organizationModule.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchOrganizationModules();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handlePageReset() {
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrl(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
    },
    dateFormatter: (value) => {
      if (!value) return "無";
      return format(new Date(value), "yyyy-MM-dd");
    },
  },
};
</script>
